import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, X } from 'lucide-react';

import Button from '@/shared/components/Button';
import Input from '@/shared/components/Input';
import MultiSelect from '@/shared/components/MultiSelect';
import Select from '@/shared/components/Select';
import Textarea from '@/shared/components/Textarea';
import ToggleSwitch from '@/shared/components/ToggleSwitch';
import '@/styles/design-system.css';

import { ROUTE_BACK_OFFICE_INSTRUCTION_BLOCKS } from '../../../constants';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';

const API_BASE = `${SERVER_URL}/api/ui/bo/instruction-blocks`;

const RULE_FIELD_OPTIONS = [
  { value: 'vertical', label: 'Vertical' },
  { value: 'intent', label: 'Intent' },
];

const RULE_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'is' },
  { value: 'not_equals', label: 'is not' },
  { value: 'in', label: 'is any of' },
];

const emptyCondition = () => ({ field: 'vertical', operator: 'equals', value: '' });

const normalizeRulesForForm = (scopeRules) =>
  (scopeRules ?? []).map((group) =>
    (group ?? []).map((condition) => ({
      ...condition,
      value: condition.operator === 'in'
        ? (condition.value ?? []).map(String)
        : String(condition.value ?? ''),
    })),
  );

const HelpText = ({ children }) => (
  <p className="mt-1.5 text-xs text-muted-foreground">{children}</p>
);

const COMPACT_TRIGGER = '!h-9 !rounded-lg !px-2.5 !text-sm';
const COMPACT_MULTI_WRAPPER = '[&_button]:!h-9 [&_button]:!rounded-lg [&_button]:!px-2.5 [&_button]:!text-sm';

const ScopeRulesBuilder = ({ rules, onChange, meta }) => {
  const valueOptions = (field) =>
    field === 'vertical'
      ? meta.verticals.map((v) => ({ value: String(v.id), label: v.name }))
      : meta.intents.map((i) => ({ value: i, label: i }));

  const updateCondition = (groupIndex, conditionIndex, patch) => {
    onChange(rules.map((group, gi) =>
      gi !== groupIndex ? group : group.map((condition, ci) => {
        if (ci !== conditionIndex) return condition;
        const next = { ...condition, ...patch };
        if (patch.field && patch.field !== condition.field) next.value = next.operator === 'in' ? [] : '';
        if (patch.operator && patch.operator !== condition.operator) {
          if (patch.operator === 'in') next.value = condition.value ? [condition.value] : [];
          else if (condition.operator === 'in') next.value = condition.value?.[0] ?? '';
        }
        return next;
      }),
    ));
  };

  const addCondition = (groupIndex) => {
    onChange(rules.map((group, gi) => (gi === groupIndex ? [...group, emptyCondition()] : group)));
  };

  const removeCondition = (groupIndex, conditionIndex) => {
    onChange(rules
      .map((group, gi) => (gi === groupIndex ? group.filter((_, ci) => ci !== conditionIndex) : group))
      .filter((group) => group.length > 0));
  };

  const addGroup = () => onChange([...rules, [emptyCondition()]]);

  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-foreground">Scope</p>
      {rules.length === 0 && (
        <p className="mb-3 rounded-lg !border !border-dashed !border-[#cbd5e1] !bg-[#f8fafc] px-4 py-3 text-sm text-muted-foreground">
          No conditions — this block applies to all content generation (common).
        </p>
      )}
      {rules.map((group, groupIndex) => (
        <div key={groupIndex}>
          {groupIndex > 0 && (
            <div className="my-2 flex items-center gap-3">
              <span className="h-px flex-1 bg-[#e2e8f0]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">or</span>
              <span className="h-px flex-1 bg-[#e2e8f0]" />
            </div>
          )}
          <div className="!rounded-lg !border !border-solid !border-[#cbd5e1] !bg-[#f8fafc] p-3">
            <div className="flex flex-col gap-2">
              {group.map((condition, conditionIndex) => (
                <div key={conditionIndex} className="flex items-start gap-1.5">
                  <span className="w-8 pt-2 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {conditionIndex === 0 ? 'if' : 'and'}
                  </span>
                  <div className="w-28 shrink-0">
                    <Select
                      value={condition.field}
                      onValueChange={(val) => updateCondition(groupIndex, conditionIndex, { field: val })}
                      options={RULE_FIELD_OPTIONS}
                      triggerClassName={COMPACT_TRIGGER}
                    />
                  </div>
                  <div className="w-32 shrink-0">
                    <Select
                      value={condition.operator}
                      onValueChange={(val) => updateCondition(groupIndex, conditionIndex, { operator: val })}
                      options={RULE_OPERATOR_OPTIONS}
                      triggerClassName={COMPACT_TRIGGER}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    {condition.operator === 'in' ? (
                      <MultiSelect
                        value={condition.value ?? []}
                        onValueChange={(val) => updateCondition(groupIndex, conditionIndex, { value: val })}
                        options={valueOptions(condition.field)}
                        placeholder="Select values"
                        searchPlaceholder="Search..."
                        noResultsText="No matches"
                        selectedCountText={(count) => `${count} selected`}
                        wrapperClassName={COMPACT_MULTI_WRAPPER}
                      />
                    ) : condition.field === 'intent' && !meta.intents?.length ? (
                      <Input
                        value={condition.value ?? ''}
                        onChange={(e) => updateCondition(groupIndex, conditionIndex, { value: e.target.value })}
                        placeholder="Intent value"
                        className="!h-9 !rounded-lg !px-2.5 !text-sm"
                      />
                    ) : (
                      <Select
                        value={condition.value ?? ''}
                        onValueChange={(val) => updateCondition(groupIndex, conditionIndex, { value: val ?? '' })}
                        options={valueOptions(condition.field)}
                        placeholder="Select value"
                        triggerClassName={COMPACT_TRIGGER}
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCondition(groupIndex, conditionIndex)}
                    className="mt-1.5 rounded p-1 text-muted-foreground transition-colors hover:!bg-[#e2e8f0] hover:text-foreground"
                    aria-label="Remove condition"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => addCondition(groupIndex)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <Plus className="size-3.5" /> AND condition
            </button>
          </div>
        </div>
      ))}
      <div className="mt-3">
        <Button variant="secondary" leftIcon={<Plus className="size-4" />} onClick={addGroup}>
          {rules.length ? 'OR group' : 'Add condition'}
        </Button>
      </div>
      <HelpText>
        Conditions in a group must all match (AND); the block applies when any group matches (OR).
        No conditions means the block always applies.
      </HelpText>
    </div>
  );
};

const InstructionBlock = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [meta, setMeta] = useState({ contentTypes: [], verticals: [], intents: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [metaRes, recordRes] = await Promise.all([
          fetch(`${API_BASE}/meta`, { credentials: 'include' }),
          fetch(id ? `${API_BASE}/${id}` : `${API_BASE}/defaultObject`, { credentials: 'include' }),
        ]);
        if (!metaRes.ok || !recordRes.ok) {
          showErrorMessage('Failed to load instruction block.');
          return;
        }
        const metaBody = await metaRes.json();
        const recordBody = await recordRes.json();
        setMeta(metaBody);
        setRecord({
          ...recordBody.item,
          contentTypeKeys: recordBody.item?.contentTypeKeys ?? [],
          scopeRules: normalizeRulesForForm(recordBody.item?.scopeRules),
        });
      } catch (error) {
        console.error('InstructionBlock: load failed', error);
        showErrorMessage('Failed to load instruction block.');
      }
    };
    load();
  }, [id]);

  const onFieldChange = (field, value) => {
    setRecord((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    if (!record.name || !record.content?.trim()) {
      showErrorMessage('Name and Content are required.');
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(record.name)) {
      showErrorMessage('Name may only contain letters, numbers, hyphens and underscores — no spaces. It is referenced from prompts as {{block:name}}.');
      return;
    }
    for (const group of record.scopeRules) {
      for (const condition of group) {
        const hasValue = condition.operator === 'in'
          ? condition.value?.length > 0
          : condition.value !== '' && condition.value != null;
        if (!hasValue) {
          showErrorMessage('Every scope condition needs a value — remove incomplete conditions or fill them in.');
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...record,
        sortOrder: Number(record.sortOrder) || 0,
      };
      const res = await fetch(id ? `${API_BASE}/${id}` : API_BASE, {
        method: id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        throw new Error(errorBody?.message || 'Failed to save instruction block.');
      }
      showSuccessMessage('Instruction block saved.');
      navigate(ROUTE_BACK_OFFICE_INSTRUCTION_BLOCKS);
    } catch (error) {
      console.error('InstructionBlock: save failed', error);
      showErrorMessage(error?.message || 'Failed to save instruction block.');
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  const requiredMark = <span className="text-destructive">*</span>;

  return (
    <div className="th !overflow-visible">
      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {id ? 'Edit Instruction Block' : 'New Instruction Block'}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Instruction text appended to generation prompts based on scope conditions.
          </p>
        </div>

        <div className="!rounded-xl !border !border-solid !border-[#cbd5e1] !bg-white p-6 !shadow-sm">
          <div className="flex flex-col gap-5">
            <div>
              <Input
                label={<>Name {requiredMark}</>}
                value={record.name ?? ''}
                onChange={(e) => onFieldChange('name', e.target.value)}
              />
              <HelpText>
                Letters, numbers, hyphens and underscores only — no spaces. Referenced from prompts
                as {'{{block:name}}'}, e.g. <strong>itinerary-guide</strong>,{' '}
                <strong>hotel-amenities-tone</strong>, <strong>restaurant-menu-style</strong>.
              </HelpText>
            </div>

            <Input
              label="Description"
              value={record.description ?? ''}
              onChange={(e) => onFieldChange('description', e.target.value)}
            />

            <ScopeRulesBuilder
              rules={record.scopeRules}
              onChange={(rules) => onFieldChange('scopeRules', rules)}
              meta={meta}
            />

            <div>
              <MultiSelect
                label="Content Types"
                value={record.contentTypeKeys ?? []}
                onValueChange={(val) => onFieldChange('contentTypeKeys', val)}
                options={meta.contentTypes.map((ct) => ({
                  value: ct.key,
                  label: `${ct.category} — ${ct.name} (${ct.key})`,
                }))}
                placeholder="All content types"
                searchPlaceholder="Search content types..."
                noResultsText="No matches"
                selectedCountText={(count) => `${count} selected`}
              />
              <HelpText>Leave empty to apply to all content generation prompts.</HelpText>
            </div>

            <div className="grid grid-cols-2 items-center gap-6">
              <div>
                <Input
                  label="Sort Order"
                  type="number"
                  value={record.sortOrder ?? 0}
                  onChange={(e) => onFieldChange('sortOrder', e.target.value)}
                />
                <HelpText>Blocks are appended in ascending sort order.</HelpText>
              </div>
              <ToggleSwitch
                label="Active"
                description="Inactive blocks are never injected"
                checked={!!record.isActive}
                onCheckedChange={(val) => onFieldChange('isActive', val)}
              />
            </div>

            <div>
              <Textarea
                label={<>Content {requiredMark}</>}
                value={record.content ?? ''}
                onChange={(e) => onFieldChange('content', e.target.value)}
                autoGrow={false}
                className="min-h-[300px] font-mono text-sm"
              />
              <HelpText>
                Instruction text appended to the system prompt. Supports {'{{variable}}'} placeholders
                — they resolve with the same variables as the base template.
              </HelpText>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => navigate(ROUTE_BACK_OFFICE_INSTRUCTION_BLOCKS)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstructionBlock;
